import { useState, useEffect, useMemo } from 'react';
import { Send } from 'lucide-react';
import ModulLayout, { PANEL_OD } from '../../layout/ModulLayout';
import useSirokyDisplej from '../../../hooks/useSirokyDisplej';
import LideListPanel from './LideListPanel';
import ZamestnanecKarta from '../karta/ZamestnanecKarta';
import ZamestnanecPanel, { PrehledZamestnance } from '../karta/ZamestnanecPanel';
import { Tlacitko, Hlaska } from '../karta/ui';
import { SECTION_COLOR, kDoreseniZamestnance } from '../lide';

/**
 * Lidé → Organizace. Norma rozvržení (§1): bez výběru seznam přes celou šířku
 * (mřížka), po výběru zúžený seznam + karta zaměstnance + třetí sloupec.
 * Dřív se karta otevírala jako modal přes seznam a mezi lidmi se nedalo
 * proklikat bez zavírání.
 */
export default function Organizace({
    user, isDark, isMobile, cardStyle, inputStyle, mutedColor,
    zamestnanci, loading, onZmena,
    certTypy, onboardingMap, dokumenty, vidiDokumenty, upravujeSeznam, upravujeDokumenty, isManager,
    rezervaDole = null,
    vybranyZvenku = null,   // proklik z Cockpitu → Můj tým (`zamestnanecId` v adrese)
}) {
    const [hledani, setHledani] = useState('');
    const [pohled, setPohled] = useState('aktivni');
    const [filtr, setFiltr] = useState({ oddeleni: '', uvazek: '', nadrizeny: '' });
    const [zobrazeni, setZobrazeni] = useState('seznam');
    const [vybranyId, setVybranyId] = useState(vybranyZvenku ? Number(vybranyZvenku) : null);
    useEffect(() => { if (vybranyZvenku) setVybranyId(Number(vybranyZvenku)); }, [vybranyZvenku]);
    const [archiv, setArchiv] = useState(null);
    const [pripominka, setPripominka] = useState({ bezi: null, hlaska: null });

    // Stojí vedle karty třetí sloupec? Karta pak nekreslí podruhé, co je v něm
    // (fotka, certifikace, doba ve firmě). Stejný práh jako `ModulLayout`,
    // který panel schovává — kdyby se rozešly, zmizelo by obojí naráz.
    const maPanel = useSirokyDisplej(PANEL_OD) && !isMobile;

    // Archiv (neaktivní) se tahá, až když se o něj někdo řekne
    useEffect(() => {
        if (pohled !== 'archiv' || archiv !== null) return;
        fetch('/api/zamestnanci?archiv=1').then(r => r.json())
            .then(d => setArchiv(Array.isArray(d) ? d.filter(z => z.stav === 'neaktivni') : []))
            .catch(() => setArchiv([]));
    }, [pohled, archiv]);

    const myId = user?.zamestnanec_id;
    const mojiPodrizeni = useMemo(() => new Set(zamestnanci.filter(z => z.nadrizeny_id === myId).map(z => z.id)), [zamestnanci, myId]);
    const smiOtevrit = (id) => upravujeSeznam || id === myId || (isManager && mojiPodrizeni.has(id));

    // Nepodepsané dokumenty po lidech — pohled „Nepodepsáno" a „K dořešení"
    const nepodepsanePo = useMemo(() => {
        const m = {};
        for (const d of dokumenty || []) {
            // Expirované se nepočítají — stejně jako v záložce Osobní dokumenty
            if (d.expiruje_at && new Date(d.expiruje_at) < new Date()) continue;
            if (d.vyzaduje_podpis && !d.podepsano_at && d.zamestnanec_id) (m[d.zamestnanec_id] = m[d.zamestnanec_id] || []).push(d);
        }
        return m;
    }, [dokumenty]);
    const nepodepsanePocty = useMemo(() => Object.fromEntries(Object.entries(nepodepsanePo).map(([k, v]) => [k, v.length])), [nepodepsanePo]);

    const vybrany = vybranyId ? (zamestnanci.find(z => z.id === vybranyId) || archiv?.find(z => z.id === vybranyId) || null) : null;
    const kDoreseni = vybrany ? kDoreseniZamestnance(vybrany, {
        certTypy, nepodepsane: nepodepsanePo[vybrany.id] || [], checklist: onboardingMap[vybrany.id],
    }) : [];

    const pripomenout = async (dok) => {
        setPripominka({ bezi: dok.id, hlaska: null });
        let hlaska;
        try {
            const res = await fetch(`/api/dokumenty/${dok.id}/pripomenout-podpis`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
            const e = res.ok ? null : await res.json().catch(() => ({}));
            hlaska = res.ok ? { typ: 'ok', text: 'Připomínka odeslána' } : { typ: 'chyba', text: e.error || 'Nepodařilo se odeslat' };
        } catch {
            hlaska = { typ: 'chyba', text: 'Chyba při odesílání' };
        }
        setPripominka({ bezi: null, hlaska: { ...hlaska, id: dok.id } });
    };

    const renderAkce = (p) => {
        if (p.druh !== 'podpis' || !upravujeDokumenty) return null;
        return (
            <>
                <Tlacitko isDark={isDark} vyska={isMobile ? 40 : 28} disabled={pripominka.bezi === p.dokument.id} onClick={() => pripomenout(p.dokument)}>
                    <Send size={13} /> {pripominka.bezi === p.dokument.id ? 'Odesílám…' : 'Připomenout podpis'}
                </Tlacitko>
                {pripominka.hlaska?.id === p.dokument.id && <Hlaska hlaska={pripominka.hlaska} />}
            </>
        );
    };

    const vyber = (id) => { if (smiOtevrit(id)) setVybranyId(id); };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout
                isMobile={isMobile}
                vybrano={!!vybranyId}
                mobilRezim
                mobilPanel={false}
                barva={SECTION_COLOR}
                isDark={isDark}
                seznam={({ siroky }) => (
                    <LideListPanel
                        siroky={siroky} kompaktni={isMobile} isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle}
                        zamestnanci={zamestnanci} archiv={archiv} loading={loading}
                        hledani={hledani} setHledani={setHledani} pohled={pohled} setPohled={setPohled}
                        filtr={filtr} setFiltr={setFiltr} zobrazeni={zobrazeni} setZobrazeni={setZobrazeni}
                        certTypy={certTypy} onboardingMap={onboardingMap} nepodepsane={nepodepsanePocty} vidiDokumenty={vidiDokumenty}
                        activeId={vybranyId} onSelect={vyber} rezervaDole={rezervaDole}
                    />
                )}
                detail={() => (
                    <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'}
                        style={isMobile ? null : cardStyle}>
                        <ZamestnanecKarta
                            zamestnanecId={vybranyId} user={user} isDark={isDark} isMobile={isMobile} maPanel={maPanel}
                            certTypy={certTypy} vsichniZamestnanci={zamestnanci}
                            kDoreseni={kDoreseni} renderAkceKDoreseni={renderAkce}
                            prehledNaMobilu={vybrany ? (
                                <PrehledZamestnance z={vybrany} certTypy={certTypy} checklist={onboardingMap[vybrany.id]} isDark={isDark} padding="14px 16px 4px" />
                            ) : null}
                            rezervaDole={isMobile ? rezervaDole : null}
                            onZpet={() => setVybranyId(null)}
                            onDataChanged={onZmena}
                        />
                    </div>
                )}
                panel={() => (vybrany ? (
                    <ZamestnanecPanel z={vybrany} certTypy={certTypy} checklist={onboardingMap[vybrany.id]}
                        kDoreseni={kDoreseni} renderAkce={renderAkce} isDark={isDark} cardStyle={cardStyle} />
                ) : null)}
            />
        </div>
    );
}
